import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, Mail, MapPin, Phone, ShieldCheck } from "lucide-react";
import { SUMMIT, ABOUT_STORY } from "@/constants/summit";
import { MaskedLine, Reveal, SectionHeading } from "@/components/Reveal";

const About = () => (
  <main data-testid="about-page" className="pt-[72px]">
    <section className="relative overflow-hidden py-20 sm:py-28">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,87,34,0.12),transparent_55%)]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <div className="mb-6 flex items-center gap-4">
              <span className="h-px w-10 bg-brand-500" />
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand-300">About the Summit</span>
            </div>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.0]">
              <MaskedLine delay={0.1}>Detroit's moment</MaskedLine>
              <MaskedLine delay={0.24}><span className="text-brand-500">to choose.</span></MaskedLine>
            </h1>
            <Reveal delay={0.4}>
              <p className="mt-6 max-w-xl text-base sm:text-lg text-white/60 leading-relaxed">
                {SUMMIT.name} is {SUMMIT.tagline} — by special invitation only. Leaders from Detroit
                and South Africa gather for one evening to shape the future of health, wellness,
                innovation and investment.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.25}>
            <div className="orange-glow overflow-hidden rounded-2xl border border-brand-500/30">
              <img
                src="/assets/hero-skyline.jpg"
                alt="Detroit skyline at night"
                className="aspect-video w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 pb-10 sm:px-8">
      <div>
        {ABOUT_STORY.map((c, i) => (
          <Reveal key={c.num} delay={i * 0.05}>
            <div className="group grid gap-4 border-t border-white/10 py-10 sm:grid-cols-12 sm:gap-8 sm:py-12 transition-colors duration-500 hover:border-brand-500/40">
              <span className="font-mono text-sm text-brand-400 sm:col-span-2">{c.num}</span>
              <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-tight sm:col-span-4 transition-colors duration-500 group-hover:text-brand-300">
                {c.title}
              </h3>
              <p className="text-base leading-relaxed text-white/55 sm:col-span-6">{c.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
      <SectionHeading index="05" kicker="Event Details" title="One evening. Grand River Avenue." />
      <div className="mt-12 grid gap-5 lg:grid-cols-2">
        <Reveal>
          <div data-testid="about-event-details" className="h-full rounded-2xl border border-white/10 bg-charcoal/70 p-8">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-500/15 border border-brand-500/30">
                  <CalendarDays className="h-5 w-5 text-brand-400" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Date</p>
                  <p className="mt-1 font-display text-lg font-semibold">{SUMMIT.dateLabel}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-500/15 border border-brand-500/30">
                  <Clock className="h-5 w-5 text-brand-400" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Program</p>
                  <p className="mt-1 font-display text-lg font-semibold">4:00–5:00 PM — Meet & Greet + Light Refreshments</p>
                  <p className="font-display text-lg font-semibold">5:00–7:00 PM — Summit Program</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-500/15 border border-brand-500/30">
                  <MapPin className="h-5 w-5 text-brand-400" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Venue</p>
                  <p className="mt-1 font-display text-lg font-semibold">{SUMMIT.venue}</p>
                  <p className="text-sm text-white/55">{SUMMIT.address}, {SUMMIT.city}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-500/15 border border-brand-500/30">
                  <ShieldCheck className="h-5 w-5 text-brand-400" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Please Note</p>
                  <p className="mt-1 text-sm leading-relaxed text-white/60">
                    Seats are strictly limited. This invitation does not guarantee admission —
                    reservation confirmation is required.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="flex h-full flex-col gap-5">
            <div className="orange-glow rounded-2xl border border-brand-500/40 bg-gradient-to-br from-brand-500 to-brand-700 p-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/80">Presented by</p>
              <h3 className="mt-3 font-display text-2xl sm:text-3xl font-black leading-tight">
                The Edutainment Box × DABO
              </h3>
              <p className="mt-3 text-sm sm:text-base text-white/85 leading-relaxed">
                Brought to you by The Edutainment Box, in collaboration with DABO Detroit.
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-charcoal/70 p-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-brand-300">Concierge Contact</p>
              <p className="mt-3 font-display text-xl font-semibold">{SUMMIT.contactName}</p>
              <div className="mt-4 space-y-3 text-sm text-white/65">
                <a data-testid="about-contact-email" href={`mailto:${SUMMIT.contactEmail}`} className="flex items-center gap-3 hover:text-brand-300 transition-colors">
                  <Mail className="h-4 w-4 text-brand-400" /> {SUMMIT.contactEmail}
                </a>
                <a data-testid="about-contact-phone" href={`tel:+1${SUMMIT.contactPhone.replace(/[^0-9]/g, "")}`} className="flex items-center gap-3 hover:text-brand-300 transition-colors">
                  <Phone className="h-4 w-4 text-brand-400" /> {SUMMIT.contactPhone}
                </a>
              </div>
              <Link
                to="/confirm-attendance"
                data-testid="about-rsvp-button"
                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-brand-500 px-6 py-3 font-display text-sm font-semibold transition-all duration-300 hover:bg-brand-600 hover:shadow-[0_0_32px_rgba(255,87,34,0.5)]"
              >
                Reserve Your Seat
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  </main>
);

export default About;
